import React, {
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { HiOutlineTruck } from 'react-icons/hi';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import * as XLSX from 'xlsx';
import styles from './home.module.scss';
import getStats from '../../actions/stats';
import { getData } from '../../actions/index';
import { DatePicker, StyledSelect } from '../../lib/HooksFormFields';

const Home = () => {
  const dispatch = useDispatch();
  const { statsReducer } = useSelector((store) => store);
  const { authReducer } = useSelector((store) => store);
  const { selectedAgency, user } = authReducer;
  const canViewTransports = ['OwnerTransport', 'Owner', 'Admin'].includes(user?.role);
  const { stats } = statsReducer;
  const [isExporting, setIsExporting] = useState(false);

  // Calculer le premier et dernier jour du mois en cours
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate, endDate };
  };

  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDates();

  const { control, watch } = useForm({
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      detail: {
        value: 'transports',
        label: 'Types de flux',
      },
    },
  });

  const options = [{
    value: 'transports',
    label: 'Types de flux',
  }, {
    value: 'trunkTypes',
    label: 'Types de camion',
  }, {
    value: 'carriers',
    label: 'Transporteurs',
  }, {
    value: 'departmentsDelivery',
    label: 'Départements de livraison (sorties)',
  }, {
    value: 'departmentsLoad',
    label: 'Départements de chargement (retours)',
  }];

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const detail = watch('detail');

  // Fonction pour convertir HSL en RGB puis en hex
  function hslToHex(h, s, l) {
    const lightness = l / 100;
    const a = (s * Math.min(lightness, 1 - lightness)) / 100;
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = lightness - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  // Fonction pour générer une couleur basée sur un nom et un type
  function generateColorFromName(name, detailType) {
    // Créer un hash du nom pour avoir une couleur cohérente
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

    // Définir les teintes de base pour chaque type
    const baseHues = {
      transports: 200, // Bleu
      trunkTypes: 220, // Bleu/violet
      carriers: 350, // Rose/magenta
      departmentsDelivery: 150, // Vert
      departmentsLoad: 10, // Orange/rouge
    };

    const baseHue = baseHues[detailType] || 200;

    // Varier la teinte en fonction du hash et de l'index
    const hueVariation = (hash % 60) - 30; // Variation de -30 à +30 degrés
    const hue = (baseHue + hueVariation + 360) % 360;

    // Varier la saturation (entre 50% et 80%)
    const saturation = 50 + (hash % 30);

    // Varier la luminosité (entre 45% et 70%)
    const lightness = 45 + (hash % 25);

    return hslToHex(hue, saturation, lightness);
  }

  // Fonction pour obtenir la couleur d'un transport ou type
  function getColorForTransport(name, detailType, index = 0, total = 1) {
    if (!name) return '#cccccc';

    // Normaliser le nom (trim et gestion des valeurs nulles/vides)
    const normalizedName = (name || '').trim();
    if (!normalizedName || normalizedName === 'null' || normalizedName === 'undefined') {
      return '#cccccc';
    }

    // Si c'est pour les types de transport (transports)
    if (detailType === 'transports') {
      // Mapping des transports aux couleurs spécifiques
      const transportColorMap = {
        'Départ par nos soins': '#5da9e5', // SORTIE MATERIELS - bleu
        'Transp. par nos soins': '#5da9e5',
        'Transport par nos soins': '#5da9e5',
        'Départ client': '#e8815d', // SORTIE MATERIELS JOINTE - orange
        'Transport client': '#e8815d',
        'Départ monteurs Lvtec': '#52cd90', // TRANSFERT DE PARC - vert
        'Transport monteurs Lvtec': '#52cd90',
        'Retour par nos soins': '#c34d98', // RETOUR MATERIELS - rose/magenta
        'Retour client': '#c34d98',
        'Retour monteurs Lvtec': '#52cd90', // vert
        'A définir': '#fcef65', // RECHERCHE EN COURS - jaune
        Franco: '#bc906f', // LIVRAISON FOURNISSEURS - beige/marron
        'Non spécifié': '#cccccc', // TRANSPORT TERMINÉ - gris
      };

      // Chercher une correspondance exacte d'abord
      if (transportColorMap[normalizedName]) {
        return transportColorMap[normalizedName];
      }

      // Chercher une correspondance partielle (insensible à la casse)
      const foundKey = Object.keys(transportColorMap).find(
        (key) => key.toLowerCase() === normalizedName.toLowerCase(),
      );
      if (foundKey) {
        return transportColorMap[foundKey];
      }

      // Pour les transports non mappés, générer une couleur dynamique
      return generateColorFromName(normalizedName, detailType, index, total);
    }

    // Pour tous les autres types, générer des couleurs dynamiquement
    return generateColorFromName(normalizedName, detailType, index, total);
  }

  // Préparer les données pour le graphique donut
  const chartData = stats?.repartition?.map((item, index, array) => {
    const itemName = item.name || 'Non spécifié';
    const detailValue = detail?.value || 'transports';
    return {
      name: itemName,
      value: item.count || 0,
      color: getColorForTransport(itemName, detailValue, index, array.length),
    };
  }).filter((item) => item.value > 0) || [];

  // Composant de tooltip personnalisé
  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className={styles.tooltip}>
          <div className={styles.tooltipContent}>
            <div
              className={styles.tooltipColor}
              style={{ backgroundColor: data.payload.color }}
            />
            <div className={styles.tooltipText}>
              <div className={styles.tooltipLabel}>{data.payload.name}</div>
              <div className={styles.tooltipValue}>{data.value}</div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Composant de légende personnalisée
  const renderCustomLegend = (props) => {
    const { payload } = props;
    if (!payload || payload.length === 0) return null;
    return (
      <div className={styles.legend}>
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendColor}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendName}>{entry.payload.name}</span>
            <span className={styles.legendValue}>{entry.payload.value}</span>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const params = {};

    if (startDate) {
      // Format date as YYYY-MM-DD for URL
      const formattedStartDate = startDate.toISOString().split('T')[0];
      params.startDate = formattedStartDate;
    }

    if (endDate) {
      // Format date as YYYY-MM-DD for URL
      const formattedEndDate = endDate.toISOString().split('T')[0];
      params.endDate = formattedEndDate;
    }

    if (detail?.value) {
      params.detail = detail.value;
    }
    getStats(dispatch, params);
  }, [selectedAgency, startDate, endDate, detail, dispatch]);

  // Fonction pour exporter les commentaires en Excel
  const handleExportTransports = async () => {
    setIsExporting(true);
    try {
      const params = {};

      // Utiliser les deux types de paramètres pour récupérer tous les transports
      // (planifiés et non planifiés) dans la période sélectionnée
      if (startDate) {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        params.startDate = formattedStartDate;
        params.filterStartDate = formattedStartDate;
      }

      if (endDate) {
        const formattedEndDate = endDate.toISOString().split('T')[0];
        params.endDate = formattedEndDate;
        params.filterEndDate = formattedEndDate;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `/transport${queryString ? `?${queryString}` : ''}`;

      const response = await getData(null, url, dispatch, true);

      if (response?.data?.transports) {
        // Filtrer les transports qui ont un commentQuality
        const transportsWithComments = response.data.transports
          .map((transport) => ({
            'statut du transport': transport.type.value || '',
            'numero prepa': transport.number || '',
            'nom du transporteur': transport.carrier?.name || '',
            'Type de camion': transport.trunkType || '',
            'numéro BC': transport.purchaseOrder || '',
            'date de chargement': transport.loadDate || '',
            'code affaire': transport.cases?.[0]?.ref || '',
            'nom client': transport.cases?.[0]?.client || '',
            'nom du chantier': transport.cases?.[0]?.name || '',
            transport: transport.transport || '',
            'ville chargement': transport.loadLocation.city || '',
            'département chargement': transport.loadLocation.cp ? transport.loadLocation.cp.slice(0, 2) : '',
            'ville livraison': transport.deliveryLocation.city || '',
            'département livraison': transport.deliveryLocation.cp ? transport.deliveryLocation.cp.slice(0, 2) : '',
            'commentaire qualité': transport.commentQuality || '',
          }));

        if (transportsWithComments.length === 0) {
          alert('Aucun transport trouvé pour la période sélectionnée.');
          setIsExporting(false);
          return;
        }

        // Créer un workbook et une feuille
        const worksheet = XLSX.utils.json_to_sheet(transportsWithComments);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Commentaires');

        // Générer le nom du fichier avec les dates
        const startDateStr = startDate ? startDate.toISOString().split('T')[0] : '';
        const endDateStr = endDate ? endDate.toISOString().split('T')[0] : '';
        const fileName = `export_transports${startDateStr ? `_${startDateStr}` : ''}${endDateStr ? `_${endDateStr}` : ''}.xlsx`;

        // Télécharger le fichier
        XLSX.writeFile(workbook, fileName);
      } else {
        alert('Erreur lors de la récupération des transports.');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des commentaires.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.home}>
      <div className={styles.container}>
        <div className={styles.kpiContainer}>
          <div className={styles.kpi}>
            <div className={styles.kpiIcon}>
              <HiOutlineTruck/>
            </div>
            <div className={styles.kpiContent}>
              <div className={styles.kpiValue}>
                {stats?.countJ0}
              </div>
              <div className={styles.kpiLabel}>
                LPS transport du jour
              </div>
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiIcon}>
              <HiOutlineTruck/>
            </div>
            <div className={styles.kpiContent}>
              <div className={styles.kpiValue}>
                {stats?.countJ1}
              </div>
              <div className={styles.kpiLabel}>
                LPS transport à J+1
              </div>
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiIcon}>
              <HiOutlineTruck/>
            </div>
            <div className={styles.kpiContent}>
              <div className={styles.kpiValue}>
                {stats?.countJ2}
              </div>
              <div className={styles.kpiLabel}>
                LPS transport à J+2
              </div>
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiIcon} style={{ color: '#B7876E', backgroundColor: 'rgba(237, 164, 132, 0.3)' }}>
              <HiOutlineTruck/>
            </div>
            <div className={styles.kpiContent}>
              <div className={styles.kpiValue}>
                {stats?.countUpdated}
              </div>
              <div className={styles.kpiLabel}>
                LPS avec dates modifiées
              </div>
            </div>
          </div>
        </div>
        {canViewTransports && (
          <div className={styles.listsContainer}>
            <div className={styles.list}>
              <div className={styles.listHeader}>
                <h2>Transports</h2>
              </div>
              <div className={styles.filtersDate}>
                <DatePicker
                  name="startDate"
                  control={control}
                  placeholder="Date de début"
                  className="primary"
                  dateFormat="dd/MM/yyyy"
                />
                <DatePicker
                  name="endDate"
                  control={control}
                  placeholder="Date de fin"
                  className="primary"
                  dateFormat="dd/MM/yyyy"
                />
                <StyledSelect
                  name="detail"
                  control={control}
                  placeholder="Afficher..."
                  className="primary"
                  options={options}
                />
                <button onClick={handleExportTransports} disabled={isExporting}>
                  {isExporting ? 'Export en cours...' : 'Exporter les transports'}
                </button>
              </div>
              {chartData.length > 0 && (
                <div className={styles.chartContainer}>
                  <div className={styles.chartWrapper}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Tooltip content={renderCustomTooltip} />
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={150}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className={styles.legendContainer}>
                    {renderCustomLegend({
                      payload: chartData.map((item) => ({
                        value: item.name,
                        color: item.color,
                        payload: item,
                      })),
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
